import React, { useState, useEffect } from 'react';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';
import voucherSettingServices from '../voucherSettingServices';
import { useAlert } from '../../../../../../context/AlertContext';
import { Save, Edit, Close, Bolt } from '@mui/icons-material';
import { IconButton, TextField, CircularProgress, Tooltip } from '@mui/material';

const VoucherSettingRow = ({ item, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        prefix: item.prefix || '',
        startingNumber: item.startingNumber || 1,
        padding: item.padding || 3
    });

    const handleSave = async () => {
        setLoading(true);
        // We keep the existing suffix from the item when updating other fields
        const success = await onUpdate({ ...item, ...formData });
        if (success) setIsEditing(false);
        setLoading(false);
    };

    return (
        <tr>
            <td className="fw-bold text-secondary">{item.voucherType}</td>
            <td>
                {isEditing ? (
                    <TextField size="small" variant="standard" value={formData.prefix} onChange={(e) => setFormData({ ...formData, prefix: e.target.value })} />
                ) : <span className="fw-bold text-primary">{item.prefix}</span>}
            </td>
            <td className="text-muted">{item.suffix}</td>
            <td>
                {isEditing ? (
                    <TextField type="number" size="small" variant="standard" value={formData.startingNumber} onChange={(e) => setFormData({ ...formData, startingNumber: e.target.value })} />
                ) : <span>{item.startingNumber}</span>}
            </td>
            <td>
                {isEditing ? (
                    <TextField type="number" size="small" variant="standard" value={formData.padding} onChange={(e) => setFormData({ ...formData, padding: e.target.value })} />
                ) : <span>{item.padding}</span>}
            </td>
            <td className="bg-light">
                <code className="small text-dark">
                    {formData.prefix}/{String(formData.startingNumber).padStart(formData.padding, '0')}/{item.suffix}
                </code>
            </td>
            <td className="text-end">
                {loading ? <CircularProgress size={20} /> : isEditing ? (
                    <>
                        <IconButton onClick={handleSave} color="success" size="small"><Save /></IconButton>
                        <IconButton onClick={() => setIsEditing(false)} color="error" size="small"><Close /></IconButton>
                    </>
                ) : (
                    <IconButton onClick={() => setIsEditing(true)} color="primary" size="small"><Edit /></IconButton>
                )}
            </td>
        </tr>
    );
};

function CompanyVoucherSettingPage() {
    const [voucherSettingList, setVoucherSettingList] = useState([]);
    const [globalSuffix, setGlobalSuffix] = useState("");
    const [bulkLoading, setBulkLoading] = useState(false);
    const { AppAlert } = useAlert();

    const getList = () => {
        voucherSettingServices.getList().then(res => {
            setVoucherSettingList(res.data);
            if (res.data.length > 0 && !globalSuffix) {
                setGlobalSuffix(res.data[0].suffix); // Pre-fill with current active suffix
            }
        });
    };

    const updateSingle = async (payload) => {
        try {
            await voucherSettingServices.save(payload);
            AppAlert(`${payload.voucherType} Updated`);
            getList();
            return true;
        } catch (e) {
            AppAlert("Update Failed", "error");
            return false;
        }
    };

    const handleBulkSuffixUpdate = () => {
        if (!globalSuffix) return AppAlert("Please enter a suffix value", "warning");
        
        setBulkLoading(true);
        voucherSettingServices.updateFinancialYear({ suffix: globalSuffix }).then(res => {
            AppAlert("Global Suffix Updated");
            getList();
        }).catch(err => {
            AppAlert("Bulk update failed", "error");
        }).finally(() => setBulkLoading(false));
    };

    useEffect(() => { getList(); }, []);

    return (
        <FinanceLayout>
            <div className="container-fluid mt-3">
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white py-3">
                        <h5 className="mb-0 fw-bold">Voucher Numbering Series</h5>
                        <p className="text-muted small mb-0">Define prefixes per type. Use the header to update Financial Year (Suffix) globally.</p>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th width="15%">Voucher Type</th>
                                    <th width="15%">Prefix</th>
                                    <th width="20%">
                                        <div className="d-flex align-items-center gap-2">
                                            <TextField 
                                                label="Global Suffix" 
                                                variant="outlined" 
                                                size="small" 
                                                value={globalSuffix} 
                                                onChange={(e) => setGlobalSuffix(e.target.value)}
                                                sx={{ backgroundColor: '#fff' }}
                                            />
                                            <Tooltip title="Apply Suffix to ALL types">
                                                <button 
                                                    className="btn btn-sm btn-warning d-flex align-items-center justify-content-center" 
                                                    onClick={handleBulkSuffixUpdate}
                                                    disabled={bulkLoading}
                                                    style={{ height: '38px' }}
                                                >
                                                    {bulkLoading ? <CircularProgress size={16} /> : <Bolt fontSize="small" />}
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </th>
                                    <th width="10%">Start No</th>
                                    <th width="10%">Padding</th>
                                    <th width="20%">Sample Preview</th>
                                    <th width="10%" className="text-end">Edit Row</th>
                                </tr>
                            </thead>
                            <tbody>
                                {voucherSettingList?.map(item => (
                                    <VoucherSettingRow key={item.id} item={item} onUpdate={updateSingle} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </FinanceLayout>
    );
}

export default CompanyVoucherSettingPage;